import { DateTime } from 'luxon';

import { BuildingUnitTenancy } from 'shared';

export interface threeMonthCleaningSchedule {
  month: string;
  numStandardCleanings: number;
  numTouchUpCleanings: number;
}

export const calculateCleaningSchedule = (
  startDate: DateTime,
  standardDay: number,
  touchUpDay: number | null
): threeMonthCleaningSchedule[] => {
  const schedule: threeMonthCleaningSchedule[] = [];

  // Calculate for the start month and the next two months
  for (let i = 0; i < 3; i++) {
    const currentMonth = startDate.plus({ months: i });
    const monthName = currentMonth.toFormat('MMMM');

    // Get the first day of the month
    const firstDayOfMonth = currentMonth.startOf('month');

    let numStandardCleanings = 0;
    let numTouchUpCleanings = 0;

    // Calculate standard cleanings for this month
    if (i === 0) {
      // For the start month, only count cleanings from the start date onwards
      // Find the first occurrence of the standard day in or after the start date
      let currentDate = startDate;
      while (currentDate.weekday !== standardDay) {
        currentDate = currentDate.plus({ days: 1 });
      }
      // Count occurrences within this month
      while (currentDate.month === currentMonth.month && currentDate.year === currentMonth.year) {
        numStandardCleanings++;
        currentDate = currentDate.plus({ weeks: 1 });
      }
    } else {
      // For subsequent months, count all standard cleaning days
      // Find the first occurrence of the standard day in this month
      let currentDate = firstDayOfMonth;
      while (currentDate.weekday !== standardDay) {
        currentDate = currentDate.plus({ days: 1 });
      }
      // Count occurrences within this month
      while (currentDate.month === currentMonth.month && currentDate.year === currentMonth.year) {
        numStandardCleanings++;
        currentDate = currentDate.plus({ weeks: 1 });
      }
    }

    // Calculate touch up cleanings for this month
    if (touchUpDay === null) {
      numTouchUpCleanings = 0;
    } else if (i === 0) {
      // For the start month, only count cleanings from the start date onwards
      // Find the first occurrence of the touch up day in or after the start date
      let currentDate = startDate;
      while (currentDate.weekday !== touchUpDay) {
        currentDate = currentDate.plus({ days: 1 });
      }
      // Count occurrences within this month
      while (currentDate.month === currentMonth.month && currentDate.year === currentMonth.year) {
        numTouchUpCleanings++;
        currentDate = currentDate.plus({ weeks: 1 });
      }
    } else {
      // For subsequent months, count all touch up cleaning days
      // Find the first occurrence of the touch up day in this month
      let currentDate = firstDayOfMonth;
      while (currentDate.weekday !== touchUpDay) {
        currentDate = currentDate.plus({ days: 1 });
      }
      // Count occurrences within this month
      while (currentDate.month === currentMonth.month && currentDate.year === currentMonth.year) {
        numTouchUpCleanings++;
        currentDate = currentDate.plus({ weeks: 1 });
      }
    }

    schedule.push({
      month: monthName,
      numStandardCleanings,
      numTouchUpCleanings
    });
  }

  return schedule;
};

export const calculateYearlyPriceAverage = (
  startDate: DateTime,
  standardDay: number,
  touchUpDay: number | null,
  touchUpCleaningPrice: number,
  standardCleaningPrice: number
) => {
  let total = 0;
  // Calculate for the start month and the next two months
  for (let i = 1; i < 13; i++) {
    const currentMonth = startDate.plus({ months: i });

    // Get the first day of the month
    const firstDayOfMonth = currentMonth.startOf('month');

    let numStandardCleanings = 0;
    let numTouchUpCleanings = 0;

    let currentDate = firstDayOfMonth;
    while (currentDate.weekday !== standardDay) {
      currentDate = currentDate.plus({ days: 1 });
    }
    // Count occurrences within this month
    while (currentDate.month === currentMonth.month && currentDate.year === currentMonth.year) {
      numStandardCleanings++;
      currentDate = currentDate.plus({ weeks: 1 });
    }

    // Calculate touch up cleanings for this month
    if (touchUpDay === null) {
      numTouchUpCleanings = 0;
    } else {
      let currentDate = firstDayOfMonth;
      while (currentDate.weekday !== touchUpDay) {
        currentDate = currentDate.plus({ days: 1 });
      }
      // Count occurrences within this month
      while (currentDate.month === currentMonth.month && currentDate.year === currentMonth.year) {
        numTouchUpCleanings++;
        currentDate = currentDate.plus({ weeks: 1 });
      }
    }

    total += numStandardCleanings * standardCleaningPrice + numTouchUpCleanings * touchUpCleaningPrice;
  }

  return total / 12;
};

export const getFirstOccurrenceOfWeekday = (targetDay: number | null, serviceStartDate: string) => {
  if (!targetDay) {
    return null;
  }

  let startDateTime = DateTime.fromISO(serviceStartDate);

  while (startDateTime.weekday !== targetDay) {
    startDateTime = startDateTime.plus({ days: 1 });
  }

  return startDateTime;
};

// Helper function to check if a tenancy is active on a given date
export const isBuildingUnitTenancyActiveOnDate = (buildingUnitTenancy: BuildingUnitTenancy, date: DateTime) => {
  const now = date.toISO();
  const startDate = buildingUnitTenancy.start_date;
  const endDate = buildingUnitTenancy.end_date;

  // Tenancy is active if:
  // 1. Start date is in the past AND end date is null (ongoing)
  // 2. Start date is in the past AND end date is in the future
  return startDate <= now && (endDate === null || endDate > now);
};
